import React, { useEffect, useState } from "react";
import { getSupplierCategories, saveSupplierCategory } from "../../components/Api/GmsSupplierCategory";
import Select from "react-select";
import { Button } from "reactstrap";
import Swal from "sweetalert2";
import { Eye } from "lucide-react";
import { ClipLoader } from "react-spinners";
import 'bootstrap/dist/css/bootstrap.min.css';
import { 
  getUserPermissions, 
  hasPagePermissionWithAdmin, 
  hasEditPermissionWithAdmin, 
  hasDeletePermissionWithAdmin 
} from "../../components/Permission/PermissionService";

// Custom styles for Select components (from PayrollProcessingPage)
const selectStyles = {
  control: (provided, state) => ({
    ...provided,
    minHeight: '38px',
    borderColor: state.isFocused ? '#556ee6' : '#ced4da',
    boxShadow: state.isFocused ? '0 0 0 0.2rem rgba(85, 110, 230, 0.25)' : 'none',
  }),
  menu: (provided) => ({
    ...provided,
    zIndex: 9999,
    maxHeight: 200,
  }),
  menuList: (provided) => ({
    ...provided,
    maxHeight: 180,
  }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isSelected ? '#556ee6' : state.isFocused ? '#f8f9fa' : 'white',
    color: state.isSelected ? 'white' : '#495057',
    '&:hover': {
      backgroundColor: state.isSelected ? '#556ee6' : '#e9ecef',
    },
  }),
};

const defaultForm = {
  SupplierCategory_Id: null,
  SupplierCategory_NameEn: "",
  SupplierCategory_NameAr: "",
  SupplierCategory_Active: true,
  SupplierCategory_CreatedDate: new Date().toISOString(),
  SupplierCategory_CreatedBy: Number(localStorage.getItem("userId")) || 1,
  SupplierCategory_ModifiedDate: new Date().toISOString(),
  SupplierCategory_ModifiedBy: Number(localStorage.getItem("userId")) || 1,
};

const emptyFilter = {
  SupplierCategory_NameEn: "",
  SupplierCategory_NameAr: "",
  SupplierCategory_Active: null,
};

export default function SupplierCategoryPage() {
  const [supplierCategories, setSupplierCategories] = useState([]);
  const [allSupplierCategories, setAllSupplierCategories] = useState([]);
  const [form, setForm] = useState(defaultForm);
  const [filter, setFilter] = useState(emptyFilter);
  const [showFilters, setShowFilters] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [viewLoadingRow, setViewLoadingRow] = useState(null);
  
  // Permission states
  const [permissions, setPermissions] = useState([]);
  const [permissionsLoading, setPermissionsLoading] = useState(true);
  const [canView, setCanView] = useState(false);
  const [canEdit, setCanEdit] = useState(false);
  const [canDelete, setCanDelete] = useState(false);

  // Load permissions first
  useEffect(() => {
    loadPermissions();
  }, []);

  // Load data after permissions are loaded
  useEffect(() => {
    if (!permissionsLoading && canView) {
      loadSupplierCategories();
    }
  }, [permissionsLoading, canView, page]);

  const loadPermissions = async () => {
    try {
      const userPermissions = await getUserPermissions();
      setPermissions(userPermissions);
      
      // Check permissions for this page
      const hasView = hasPagePermissionWithAdmin(userPermissions, '/GmsSupplierCategory');
      const hasEdit = hasEditPermissionWithAdmin(userPermissions, '/GmsSupplierCategory');
      const hasDelete = hasDeletePermissionWithAdmin(userPermissions, '/GmsSupplierCategory');
      
      setCanView(hasView);
      setCanEdit(hasEdit);
      setCanDelete(hasDelete);
      
      // If no view permission, redirect to dashboard
      if (!hasView) {
        Swal.fire("Access Denied", "You don't have permission to view this page.", "error").then(() => {
          window.location.href = '/Dashboard';
        });
      }
    } catch (error) {
      console.error("Error loading permissions:", error);
      Swal.fire("Error", "Failed to load permissions. Redirecting to dashboard.", "error").then(() => {
        window.location.href = '/Dashboard';
      });
    } finally {
      setPermissionsLoading(false);
    }
  };

  const loadSupplierCategories = async (filterData = emptyFilter) => {
    setLoading(true);
    try {
      const data = await getSupplierCategories(filterData);
      // Normalize data: convert camelCase from API to handle both formats
      const normalizedData = data.map(category => ({
        // Keep both formats for compatibility
        supplierCategory_Id: category.supplierCategory_Id || category.SupplierCategory_Id,
        supplierCategory_NameEn: category.supplierCategory_NameEn || category.SupplierCategory_NameEn || '',
        supplierCategory_NameAr: category.supplierCategory_NameAr || category.SupplierCategory_NameAr || '',
        supplierCategory_Active: category.supplierCategory_Active !== undefined ? category.supplierCategory_Active : category.SupplierCategory_Active,
        supplierCategory_CreatedDate: category.supplierCategory_CreatedDate || category.SupplierCategory_CreatedDate,
        supplierCategory_CreatedBy: category.supplierCategory_CreatedBy || category.SupplierCategory_CreatedBy,
        supplierCategory_ModifiedDate: category.supplierCategory_ModifiedDate || category.SupplierCategory_ModifiedDate,
        supplierCategory_ModifiedBy: category.supplierCategory_ModifiedBy || category.SupplierCategory_ModifiedBy,
        // Also keep PascalCase for backward compatibility
        SupplierCategory_Id: category.supplierCategory_Id || category.SupplierCategory_Id,
        SupplierCategory_NameEn: category.supplierCategory_NameEn || category.SupplierCategory_NameEn || '',
        SupplierCategory_NameAr: category.supplierCategory_NameAr || category.SupplierCategory_NameAr || '',
        SupplierCategory_Active: category.supplierCategory_Active !== undefined ? category.supplierCategory_Active : category.SupplierCategory_Active,
      }));
      setAllSupplierCategories(normalizedData);
      const startIndex = (page - 1) * pageSize;
      const endIndex = startIndex + pageSize;
      const paginatedData = normalizedData.slice(startIndex, endIndex);
      setSupplierCategories(paginatedData);
      setTotalCount(normalizedData.length);
    } catch (error) {
      Swal.fire("Error", "Failed to load supplier categories: " + error.message, "error");
    } finally {
      setLoading(false);
    }
  };

  const handleFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm({
      ...form,
      [name]: type === "checkbox" ? checked : value,
    });
  };

  // Set default active status based on permissions
  useEffect(() => {
    if (!isEditing && canEdit && !canDelete) {
      setForm(prev => ({
        ...prev,
        SupplierCategory_Active: true
      }));
    }
  }, [isEditing, canEdit, canDelete]);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilter({
      ...filter,
      [name]: value === "" ? null : value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const formData = {
        ...form,
        SupplierCategory_ModifiedDate: new Date().toISOString(),
        SupplierCategory_ModifiedBy: Number(localStorage.getItem("userId")) || 1,
      };
      const result = await saveSupplierCategory(formData);
      Swal.fire("Success", result.message, "success");
      setForm(defaultForm);
      setIsEditing(false);
      loadSupplierCategories(filter);
    } catch (error) {
      Swal.fire("Error", "Failed to save supplier category: " + error.message, "error");
    } finally {
      setLoading(false);
    }
  };

  const handleView = async (category) => {
    const categoryId = category.supplierCategory_Id || category.SupplierCategory_Id;
    const nameEn = category.supplierCategory_NameEn || category.SupplierCategory_NameEn || 'N/A';
    const nameAr = category.supplierCategory_NameAr || category.SupplierCategory_NameAr || 'N/A';
    const isActive = category.supplierCategory_Active !== undefined ? category.supplierCategory_Active : category.SupplierCategory_Active;
    
    setViewLoadingRow(categoryId);
    try {
      Swal.fire({
        title: nameEn,
        html: `
          <div class="text-left">
            <div class="mb-3">
              <h6 class="mb-2">Basic Information</h6>
              <p><strong>Name (English):</strong> ${nameEn}</p>
              <p><strong>Name (Arabic):</strong> ${nameAr}</p>
              <p><strong>Status:</strong> ${isActive ? "Active" : "Inactive"}</p>
            </div>
          </div>
        `,
        icon: "info",
        width: '600px',
        customClass: {
          container: 'custom-swal-container',
          popup: 'custom-swal-popup',
          content: 'custom-swal-content'
        }
      });
    } catch (error) {
      Swal.fire("Error", "Failed to view supplier category details", "error");
    } finally {
      setViewLoadingRow(null);
    }
  };

  const handleEdit = (category) => {
    // Normalize the category data to PascalCase for the form
    setForm({
      SupplierCategory_Id: category.supplierCategory_Id || category.SupplierCategory_Id,
      SupplierCategory_NameEn: category.supplierCategory_NameEn || category.SupplierCategory_NameEn || '',
      SupplierCategory_NameAr: category.supplierCategory_NameAr || category.SupplierCategory_NameAr || '',
      SupplierCategory_Active: category.supplierCategory_Active !== undefined ? category.supplierCategory_Active : category.SupplierCategory_Active,
      SupplierCategory_CreatedDate: category.supplierCategory_CreatedDate || category.SupplierCategory_CreatedDate || new Date().toISOString(),
      SupplierCategory_CreatedBy: category.supplierCategory_CreatedBy || category.SupplierCategory_CreatedBy || Number(localStorage.getItem("userId")) || 1,
      SupplierCategory_ModifiedDate: new Date().toISOString(),
      SupplierCategory_ModifiedBy: Number(localStorage.getItem("userId")) || 1,
    });
    setIsEditing(true);
    window.scrollTo(0, 0);
  };

  const handleCancelEdit = () => {
    setForm(defaultForm);
    setIsEditing(false);
  };

  const handleApplyFilter = () => {
    setPage(1);
    loadSupplierCategories(filter);
  };

  const handleClearFilter = () => {
    setFilter(emptyFilter);
    setPage(1);
    loadSupplierCategories(emptyFilter);
  };

  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  // Generate options for searchable dropdowns
  const categoryNameEnOptions = allSupplierCategories.map(c => ({
    value: c.supplierCategory_NameEn || c.SupplierCategory_NameEn || '',
    label: c.supplierCategory_NameEn || c.SupplierCategory_NameEn || ''
  }));

  const categoryNameArOptions = allSupplierCategories.map(c => ({
    value: c.supplierCategory_NameAr || c.SupplierCategory_NameAr || '',
    label: c.supplierCategory_NameAr || c.SupplierCategory_NameAr || ''
  }));

  const statusOptions = [
    { value: true, label: "Active" },
    { value: false, label: "Inactive" },
  ];

  const handleSelectChange = (selectedOption, field) => {
    setFilter({ 
      ...filter, 
      [field]: selectedOption ? selectedOption.value : null 
    });
  };

  // Show loading spinner while permissions are loading
  if (permissionsLoading) {
    return (
      <div className="page-content">
        <div className="container-fluid">
          <div className="card">
            <div className="card-body text-center py-5">
              <ClipLoader size={50} color="#556ee6" />
              <p className="mt-3">Loading permissions...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Don't render if no view permission
  if (!canView) {
    return null;
  }

  return (
    <div className="page-content">
      <div className="container-fluid">
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h4 className="mb-0">Supplier Category Management</h4>
        </div>

        {/* Form Section - Only show if user has edit permission */}
        {canEdit && (
          <div className="card mb-4">
            <div className="card-header">
              <h5 className="card-title mb-0">
                {isEditing ? "Edit Supplier Category" : "Supplier Category Details"}
              </h5>
            </div>
            <div className="card-body">
            <form onSubmit={handleSubmit}>
              <div className="row g-3 mb-3">
                <div className="col-md-6">
                  <label className="form-label">Name (English)</label>
                  <input
                    className="form-control"
                    name="SupplierCategory_NameEn"
                    placeholder="Name in English"
                    value={form.SupplierCategory_NameEn}
                    onChange={handleFormChange}
                    required
                  />
                </div>
                <div className="col-md-6">
                  <label className="form-label">Name (Arabic)</label>
                  <input
                    className="form-control"
                    name="SupplierCategory_NameAr"
                    placeholder="Name in Arabic"
                    value={form.SupplierCategory_NameAr}
                    onChange={handleFormChange}
                  />
                </div>
              </div>

              <div className="row g-3 mb-3">
                <div className="col-md-6">
                  <div className="form-check form-switch">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id="supplierCategory_Active"
                      name="SupplierCategory_Active"
                      checked={form.SupplierCategory_Active}
                      onChange={handleFormChange}
                      disabled={!canDelete} // Only allow status change if user has delete permission
                    />
                    <label className="form-check-label" htmlFor="supplierCategory_Active">{form.SupplierCategory_Active ? "Active" : "Inactive"}</label>
                  </div>
                </div>
              </div>

              <div className="d-flex justify-content-end">
                <button 
                  className="btn btn-success me-2" 
                  type="submit" 
                  disabled={loading}
                >
                  <i className="bx bx-check-circle me-2"></i>
                  {loading ? "Saving..." : (isEditing ? "Update Supplier Category" : "Save Supplier Category")}
                </button>
                {isEditing && (
                  <button 
                    className="btn btn-danger" 
                    type="button" 
                    onClick={handleCancelEdit}
                  >
                    <i className="bx bx-x-circle me-2"></i>
                    Cancel
                  </button>
                )}
              </div>
            </form>
          </div>
        </div>
        )}

        {/* Filter Section */}
        <div className="card mb-4">
          <div className="card-header">
            <div className="d-flex justify-content-between align-items-center">
              <h5 className="card-title mb-0">Filter Supplier Categories</h5>
              <button
                className="btn btn-primary d-flex align-items-center"
                onClick={() => setShowFilters(!showFilters)}
              >
                <i className="bx bx-filter me-2"></i>
                {showFilters ? "Hide Filters" : "Show Filters"}
              </button>
            </div>
          </div>
          {showFilters && (
            <div className="card-body">
              <div className="row g-3 mb-3">
                <div className="col-md-4">
                  <label className="form-label">Name (English)</label>
                  <Select
                    value={categoryNameEnOptions.find(option => option.value === filter.SupplierCategory_NameEn)}
                    onChange={(option) => handleSelectChange(option, 'SupplierCategory_NameEn')}
                    options={categoryNameEnOptions}
                    isClearable
                    placeholder="Select Name (English)"
                    styles={selectStyles}
                  />
                </div>
                <div className="col-md-4">
                  <label className="form-label">Name (Arabic)</label>
                  <Select
                    value={categoryNameArOptions.find(option => option.value === filter.SupplierCategory_NameAr)}
                    onChange={(option) => handleSelectChange(option, 'SupplierCategory_NameAr')}
                    options={categoryNameArOptions}
                    isClearable
                    placeholder="Select Name (Arabic)"
                    styles={selectStyles}
                  />
                </div>
                <div className="col-md-4">
                  <label className="form-label">Status</label>
                  <Select
                    value={filter.SupplierCategory_Active !== null 
                      ? statusOptions.find(option => option.value === filter.SupplierCategory_Active)
                      : null}
                    onChange={(option) => handleSelectChange(option, 'SupplierCategory_Active')}
                    options={statusOptions}
                    isClearable
                    placeholder="Select Status"
                    styles={selectStyles}
                  />
                </div>
              </div>

              <div className="d-flex justify-content-end">
                <button 
                  className="btn btn-primary me-2" 
                  onClick={handleApplyFilter} 
                  disabled={loading}
                >
                  <i className="bx bx-search me-2"></i>
                  Apply Filter
                </button>
                <button 
                  className="btn btn-warning" 
                  onClick={handleClearFilter}
                >
                  <i className="bx bx-refresh me-2"></i>
                  Clear Filter
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Table Section */}
        <div className="card">
          <div className="card-header">
            <h5 className="card-title mb-0">Supplier Category List ({totalCount})</h5>
          </div>
          <div className="card-body">
            {loading ? (
              <div className="text-center py-4">
                <div className="spinner-border" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
              </div>
            ) : (
              <div className="table-responsive">
                <table className="table table-bordered table-nowrap dt-responsive nowrap w-100 dataTable no-footer dtr-inline">
                  <thead className="table-light">
                    <tr>
                      <th>#</th>
                      <th>Name (English)</th>
                      <th>Name (Arabic)</th>
                      <th>Active</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {supplierCategories.length === 0 ? (
                      <tr>
                        <td colSpan="5" className="text-center">No supplier categories found</td>
                      </tr>
                    ) : (
                      supplierCategories.map((category, index) => {
                        const categoryId = category.supplierCategory_Id || category.SupplierCategory_Id;
                        const nameEn = category.supplierCategory_NameEn || category.SupplierCategory_NameEn || '';
                        const nameAr = category.supplierCategory_NameAr || category.SupplierCategory_NameAr || '';
                        const isActive = category.supplierCategory_Active !== undefined ? category.supplierCategory_Active : category.SupplierCategory_Active;
                        
                        return (
                          <tr key={categoryId}>
                            <td>{(page - 1) * pageSize + index + 1}</td>
                            <td>{nameEn}</td>
                            <td>{nameAr || 'N/A'}</td>
                            <td>
                              <span className={`badge ${isActive ? 'bg-success' : 'bg-danger'}`}>
                                {isActive ? "Yes" : "No"}
                              </span>
                            </td>
                            <td>
                              <div className="d-flex gap-2">
                                {canEdit && (
                                  <button
                                    className="btn btn-info btn-sm"
                                    onClick={() => handleEdit(category)}
                                  >
                                    <i className="bx bx-edit me-1"></i>
                                    Edit
                                  </button>
                                )}
                                <button
                                  className="btn btn-primary btn-sm"
                                  onClick={() => handleView(category)}
                                  disabled={viewLoadingRow === categoryId}
                                >
                                  {viewLoadingRow === categoryId ? (
                                    <ClipLoader size={16} color="white" />
                                  ) : (
                                    <>
                                      <Eye size={16} color="white" />
                                      <span className="ms-1">View</span>
                                    </>
                                  )}
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            )}
            <div className="d-flex justify-content-between align-items-center mt-4 p-3" 
                 style={{backgroundColor: '#f8f9fa', borderRadius: '0.375rem', border: '1px solid #dee2e6'}}>
              <div className="text-muted" style={{fontSize: '14px', fontWeight: '500'}}>
                <i className="mdi mdi-information-outline me-2" style={{color: '#556ee6'}}></i>
                Showing <span style={{color: '#556ee6', fontWeight: '600'}}>{totalCount > 0 ? (page - 1) * pageSize + 1 : 0}</span> to <span style={{color: '#556ee6', fontWeight: '600'}}>{Math.min(page * pageSize, totalCount)}</span> of <span style={{color: '#556ee6', fontWeight: '600'}}>{totalCount}</span> entries
              </div>
              {totalCount > pageSize && (
                <div style={{display: 'flex', alignItems: 'center', gap: '8px'}}>
                  <Button
                    color="light"
                    size="sm"
                    disabled={page === 1}
                    onClick={() => handlePageChange(page - 1)}
                    style={{
                      border: '1px solid #dee2e6',
                      borderRadius: '0.375rem',
                      minWidth: '32px',
                      height: '32px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      backgroundColor: page === 1 ? '#f8f9fa' : '#fff',
                      color: page === 1 ? '#6c757d' : '#495057'
                    }}
                  >
                    <i className="mdi mdi-chevron-left"></i>
                  </Button>
                  
                  {[...Array(Math.ceil(totalCount / pageSize))].map((_, index) => {
                    const pageNum = index + 1;
                    const isActive = page === pageNum;
                    
                    return (
                      <Button
                        key={pageNum}
                        color={isActive ? "primary" : "light"}
                        size="sm"
                        onClick={() => handlePageChange(pageNum)}
                        style={{
                          border: '1px solid #dee2e6',
                          borderRadius: '0.375rem',
                          minWidth: '32px',
                          height: '32px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          backgroundColor: isActive ? '#556ee6' : '#fff',
                          borderColor: isActive ? '#556ee6' : '#dee2e6',
                          color: isActive ? '#fff' : '#495057',
                          fontWeight: isActive ? '600' : '400',
                          boxShadow: isActive ? '0 2px 4px rgba(85,110,230,0.25)' : 'none'
                        }}
                      >
                        {pageNum}
                      </Button>
                    );
                  })}
                  
                  <Button
                    color="light"
                    size="sm"
                    disabled={page === Math.ceil(totalCount / pageSize)}
                    onClick={() => handlePageChange(page + 1)}
                    style={{
                      border: '1px solid #dee2e6',
                      borderRadius: '0.375rem',
                      minWidth: '32px',
                      height: '32px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      backgroundColor: page === Math.ceil(totalCount / pageSize) ? '#f8f9fa' : '#fff',
                      color: page === Math.ceil(totalCount / pageSize) ? '#6c757d' : '#495057'
                    }}
                  >
                    <i className="mdi mdi-chevron-right"></i>
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

